import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";

/**
 * Admin sends a Pedigree Addition back to the submitter with revision
 * notes. The submission flips to `revisions_requested` (not approved, not
 * rejected) and the submitter receives an inbox message containing the
 * notes verbatim.
 */
export default function RequestRevisionsDialog({ open, onClose, addition, onSent }) {
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    setNotes("");
    onClose();
  };

  const submit = async () => {
    if (!notes.trim()) {
      toast.error("Revision notes are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/pedigree-additions/${addition.id}/request-revisions`, {
        reason: notes.trim(),
      });
      toast.success("Sent back with notes — submitter has been notified");
      setNotes("");
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!addition) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg" data-testid="request-revisions-dialog">
        <DialogHeader>
          <DialogTitle>Send back with notes</DialogTitle>
          <DialogDescription>
            The submission for{" "}
            <span className="font-semibold text-ink">{addition.attached_dog_name}</span>{" "}
            will move to <span className="font-mono">revisions_requested</span> and the
            submitter receives your notes in their inbox. They keep the original entries
            and can review what you flagged.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
            What needs to change? *
          </Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={6}
            maxLength={1000}
            placeholder="e.g. The DOB on the sire looks wrong — please verify and resubmit. An external registration # would also help."
            className="mt-1 rounded-lg"
            data-testid="request-revisions-notes"
          />
          <p className="mt-1 text-[11px] text-[#6B6B6B] text-right">{notes.length}/1000</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="rounded-full bg-[#C9A227] hover:bg-[#A88500] text-white"
            data-testid="request-revisions-submit"
          >
            {submitting ? "Sending…" : "Send back to submitter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
